import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { VoiceModel, PlatformVoice } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useVoiceModels() {
  return useQuery({
    queryKey: queryKeys.voiceStudio.voiceModels(),
    queryFn: async () => {
      const { voiceModels } = await api.listVoiceModels();
      return voiceModels;
    },
    staleTime: 30_000,
  });
}

export function usePlatformVoices() {
  return useQuery({
    queryKey: queryKeys.voiceStudio.platformVoices(),
    queryFn: async () => {
      const { voices } = await api.listPlatformVoices();
      return voices;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateVoiceModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; sampleFileIds: string[] }) =>
      api.createVoiceModel(body),
    onSuccess: ({ voiceModel }) => {
      queryClient.setQueryData<VoiceModel[]>(queryKeys.voiceStudio.voiceModels(), (prev = []) => [
        voiceModel,
        ...prev,
      ]);
    },
  });
}

export function useDeleteVoiceModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) => api.deleteVoiceModel(modelId),
    onSuccess: (_data, modelId) => {
      queryClient.setQueryData<VoiceModel[]>(queryKeys.voiceStudio.voiceModels(), (prev = []) =>
        prev.filter((m) => m.voiceModelId !== modelId),
      );
    },
  });
}

export type { VoiceModel, PlatformVoice };
